(() => {
  const namespace = '__lmbHomeHeroSlider';
  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const selector = '[data-lmb-hero-slider]';
  const instances = new Map();

  class HeroSlider {
    constructor(root) {
      this.root = root;
      this.images = Array.from(root.querySelectorAll('[data-lmb-hero-image]'));
      this.parts = Array.from(root.querySelectorAll('[data-lmb-hero-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-lmb-hero-nav]'));
      this.controls = root.querySelector('[data-lmb-hero-controls]');
      this.pause = root.querySelector('[data-lmb-hero-pause]');
      this.status = root.querySelector('[data-lmb-hero-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      this.index = 0;
      this.duration = 6000;
      this.remaining = this.duration;
      this.timer = null;
      this.deadline = 0;
      this.preloadTimer = null;
      this.sequence = 0;
      this.loading = false;
      this.destroyed = false;
      this.paused = this.motion.matches;
      this.hovered = false;
      this.ready = false;
      this.inView = !('IntersectionObserver' in window);
      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver(entries => { this.inView = entries.some(entry => entry.isIntersecting); this.syncTimer(); }, { threshold: 0.05 });
        this.viewportObserver.observe(root.querySelector('.lmb-home__hero-media'));
      }
      this.pendingImages = new Map();

      const options = { signal: this.controller.signal };
      root.querySelector('[data-lmb-hero-previous]').addEventListener('click', () => this.go(this.index - 1, true), options);
      root.querySelector('[data-lmb-hero-next]').addEventListener('click', () => this.go(this.index + 1, true), options);
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.lmbHeroNav), true), options));
      this.pause.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updatePause();
        this.syncTimer();
      }, options);
      root.addEventListener('focusin', (event) => {
        if (!root.contains(event.relatedTarget)) {
          this.paused = true;
          this.updatePause();
          this.stop();
        }
      }, options);
      root.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') {
          this.hovered = true;
          this.stop();
        }
      }, options);
      root.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch') {
          this.hovered = false;
          this.syncTimer();
        }
      }, options);
      this.controls.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowLeft' ? -1 : 1), true);
        }
      }, options);
      document.addEventListener('visibilitychange', () => this.syncTimer(), options);
      this.motion.addEventListener('change', (event) => {
        if (event.matches) {
          this.paused = true;
          this.updatePause();
          this.stop();
        }
      }, options);

      this.controls.hidden = false;
      this.updatePause();
      this.prepare(0).then((ready) => {
        if (this.destroyed) return;
        if (this.index === 0) this.ready = ready;
        requestAnimationFrame(() => requestAnimationFrame(() => { if (!this.destroyed) root.dataset.lmbHeroReady = 'true'; }));
        this.syncTimer();
        this.preloadTimer = window.setTimeout(async () => {
          if (!this.destroyed) await this.prepare(1);
        }, 4000);
      });
    }

    updatePause() {
      this.pause.dataset.paused = String(this.paused);
      this.pause.setAttribute('aria-label', this.paused ? this.pause.dataset.playLabel : this.pause.dataset.pauseLabel);
    }

    stop() {
      if (this.timer !== null) {
        this.remaining = Math.max(0, this.deadline - performance.now());
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    syncTimer() {
      if (this.destroyed || !this.ready || !this.inView || this.paused || this.hovered || document.hidden || this.loading) {
        this.stop();
        return;
      }
      if (this.timer !== null) return;
      this.deadline = performance.now() + this.remaining;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.remaining = 0;
        this.go(this.index + 1, false);
      }, this.remaining);
    }

    prepare(index) {
      const slide = this.images[index];
      if (!slide || this.destroyed) return Promise.resolve(false);
      const template = slide.querySelector('template');
      if (template) {
        slide.appendChild(template.content.cloneNode(true));
        template.remove();
      }
      const image = slide.querySelector('img');
      if (!image) return Promise.resolve(false);
      if (image.complete && image.naturalWidth > 0) return this.decode(image);
      if (this.pendingImages.has(index)) return this.pendingImages.get(index);
      if (index !== 0) {
        image.loading = 'eager';
        image.fetchPriority = 'low';
      }
      const promise = new Promise((resolve) => {
        let complete = false;
        const finish = async (loaded) => {
          if (complete) return;
          complete = true;
          window.clearTimeout(timeout);
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onError);
          this.controller.signal.removeEventListener('abort', onAbort);
          const result = loaded && await this.decode(image);
          this.pendingImages.delete(index);
          resolve(result && !this.destroyed);
        };
        const onLoad = () => finish(image.naturalWidth > 0);
        const onError = () => finish(false);
        const onAbort = () => finish(false);
        const timeout = index === 0 ? null : window.setTimeout(() => finish(false), 12000);
        image.addEventListener('load', onLoad);
        image.addEventListener('error', onError);
        this.controller.signal.addEventListener('abort', onAbort, { once: true });
        if (image.complete) finish(image.naturalWidth > 0);
      });
      this.pendingImages.set(index, promise);
      return promise;
    }

    async decode(image) {
      if (typeof image.decode === 'function') {
        try {
          await image.decode();
        } catch {
          return image.complete && image.naturalWidth > 0;
        }
      }
      return image.complete && image.naturalWidth > 0;
    }

    async go(position, manual) {
      const target = (position + this.images.length) % this.images.length;
      if (this.destroyed) return;
      this.stop();
      const sequence = ++this.sequence;
      if (target === this.index) {
        this.loading = false;
        if (manual) this.remaining = this.duration;
        this.syncTimer();
        return;
      }
      this.loading = true;
      const ready = await this.prepare(target);
      if (this.destroyed || sequence !== this.sequence) return;
      this.loading = false;
      if (!manual && (this.paused || this.hovered || document.hidden)) {
        this.remaining = 0;
        this.syncTimer();
        return;
      }
      this.remaining = this.duration;
      if (!ready) {
        this.syncTimer();
        return;
      }
      this.index = target;
      this.ready = true;
      this.parts.forEach((part) => {
        const active = Number(part.dataset.lmbHeroSlide) === target;
        part.dataset.active = String(active);
        part.setAttribute('aria-hidden', String(!active));
        part.inert = !active;
      });
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === target)));
      if (manual) {
        const positionLabel = this.root.dataset.positionLabel.replace('[current]', String(target + 1)).replace('[total]', String(this.images.length));
        this.status.textContent = `${this.dots[target].getAttribute('aria-label')}. ${positionLabel}`;
      }
      this.prepare((target + 1) % this.images.length);
      this.syncTimer();
    }

    destroy() {
      this.destroyed = true;
      this.sequence += 1;
      this.stop();
      window.clearTimeout(this.preloadTimer);
      this.viewportObserver?.disconnect();
      this.controller.abort();
      this.controls.hidden = true;
    }
  }

  function init(scope) {
    for (const [root, instance] of instances) {
      if (!root.isConnected) {
        instance.destroy();
        instances.delete(root);
      }
    }
    const roots = scope.matches && scope.matches(selector) ? [scope] : Array.from(scope.querySelectorAll(selector));
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new HeroSlider(root));
    });
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, instance] of instances) {
      if (event.target === root || event.target.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  });
  window[namespace] = { init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();

